/**
 * Búsqueda de entregas por cliente, ruta o territorio.
 *
 * El matching es por **subcadena** sobre texto normalizado, y eso lo decide el
 * dato, no la comodidad: en el maestro el mismo apellido está guardado con dos
 * codificaciones distintas —una correcta y otras con UTF-8 leído como latin-1—,
 * así que buscar el término completo correcto deja fuera los registros rotos.
 * `ORDOÑEZ` encuentra 1 de 3; `ORDO` encuentra los 3.
 *
 * Normalizar tildes sigue siendo necesario para los nombres bien escritos
 * (`UNIÓN`, `CARPINTERÍA`, `BENDICIÓN`), aunque **no rescata** a los corruptos:
 * `ORDOÃEZ` normalizado da `ORDOAEZ`, no `ORDONEZ`. Esa limitación es del dato y
 * la búsqueda no puede arreglarla.
 *
 * Módulo puro: entran textos, sale un booleano. Sin estado, sin Angular.
 */

/** Cuántas coincidencias hay en cada lista donde puede estar una entrega. */
export interface SearchCounts {
  routes: number;
  unassigned: number;
  excluded: number;
}

/**
 * Texto comparable: sin tildes, en mayúsculas y sin espacios en los extremos.
 *
 * `NFD` separa la letra de su marca diacrítica y el reemplazo borra las marcas,
 * de modo que `Ó` queda como `O`.
 */
export function normalizeForSearch(text?: string): string {
  if (!text) return '';
  return text
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .trim()
    .toUpperCase();
}

/**
 * ¿Alguno de los campos contiene la consulta?
 *
 * Una consulta vacía coincide con todo: la ausencia de búsqueda no filtra.
 */
export function matchesSearch(query: string, fields: readonly (string | undefined)[]): boolean {
  const needle = normalizeForSearch(query);
  if (!needle) return true;
  return (fields ?? []).some((f) => normalizeForSearch(f).includes(needle));
}

/**
 * Resumen de dónde apareció lo buscado: `"3 en rutas · 1 sin asignar"`.
 *
 * El estado de un pedido —planificado, sin cupo, sin coordenadas— suele ser LA
 * pregunta, así que tenerlo arriba evita recorrer la pantalla para responderla.
 * Las listas en cero se omiten: un `0 excluidos` es ruido.
 */
export function searchSummary(counts: SearchCounts): string {
  const parts: string[] = [];
  if (counts.routes > 0) parts.push(`${counts.routes} en rutas`);
  if (counts.unassigned > 0) parts.push(`${counts.unassigned} sin asignar`);
  if (counts.excluded > 0) {
    parts.push(`${counts.excluded} excluido${counts.excluded === 1 ? '' : 's'}`);
  }
  return parts.join(' · ');
}
